import React, { useState, useRef, useEffect } from 'react';
import {MwSmallIcon} from "../../components/v2/icons";
import CallToActionSmall from "../../components/v2/call-to-action-sm";
import Dialog from './dialog';
import PuzzleGameForm from '../puzzle-game-form';

interface Position {
  row: number;
  col: number;
}

interface Word {
  word: string;
  start: Position;
  end: Position;
  found: boolean;
  highlightColor: number;
}

interface WordSearchGameProps {
  className?: string;
}

const GRID_SIZE = 15;
const WORDS_TO_FIND = [
  'ALERTING', 'LATENCY', 'ANALYSIS', 'LOGS', 'ANOMALY', 'METRICS',
  'CAPACITY', 'MONITORING', 'CONTEXT', 'ROOTCAUSE', 'DASHBOARD',
  'SATURATION', 'DEBUGGING', 'TELEMETRY', 'ERRORRATE', 'THRESHOLD',
  'INSIGHTS', 'TRACING'
];

// Original grid layout - keeping it exactly as it was
const SAMPLE_GRID = [
  ['M', 'G', 'N', 'G', 'S', 'A', 'T', 'U', 'R', 'A', 'T', 'I', 'O', 'N', 'C'],
  ['O', 'R', 'A', 'L', 'N', 'T', 'N', 'I', 'A', 'L', 'S', 'D', 'A', 'A', 'I'],
  ['N', 'E', 'E', 'N', 'O', 'I', 'H', 'A', 'D', 'Y', 'E', 'G', 'P', 'T', 'E'],
  ['I', 'E', 'R', 'E', 'M', 'G', 'T', 'A', 'L', 'B', 'I', 'A', 'I', 'H', 'E'],
  ['T', 'C', 'A', 'R', 'T', 'E', 'S', 'R', 'U', 'Y', 'C', 'X', 'S', 'R', 'M'],
  ['O', 'L', 'G', 'G', 'O', 'H', 'N', 'G', 'E', 'I', 'S', 'Y', 'O', 'E', 'G'],
  ['R', 'V', 'R', 'L', 'B', 'R', 'G', 'N', 'T', 'L', 'C', 'I', 'T', 'S', 'L'],
  ['I', 'L', 'O', 'O', 'L', 'I', 'R', 'Y', 'R', 'E', 'A', 'R', 'S', 'H', 'I'],
  ['N', 'O', 'A', 'A', 'N', 'O', 'M', 'A', 'L', 'Y', 'I', 'E', 'H', 'O', 'N'],
  ['G', 'R', 'A', 'G', 'G', 'V', 'T', 'U', 'T', 'C', 'M', 'S', 'L', 'L', 'S'],
  ['D', 'R', 'O', 'O', 'T', 'C', 'A', 'U', 'S', 'E', 'T', 'O', 'N', 'D', 'E'],
  ['L', 'U', 'A', 'S', 'N', 'H', 'Y', 'C', 'N', 'E', 'T', 'A', 'L', 'A', 'G'],
  ['T', 'X', 'E', 'T', 'N', 'O', 'C', 'R', 'A', 'N', 'R', 'G', 'O', 'S', 'A'],
  ['G', 'N', 'I', 'C', 'A', 'R', 'T', 'E', 'L', 'E', 'M', 'E', 'T', 'R', 'Y'],
  ['R', 'R', 'R', 'O', 'S', 'T', 'H', 'G', 'I', 'S', 'N', 'I', 'R', 'P', 'S']
];

// Function to find all possible word positions in the grid
const findWordInGrid = (word: string, grid: string[][]): { start: Position; end: Position }[] => {
  const positions: { start: Position; end: Position }[] = [];
  const wordUpper = word.toUpperCase();
  
  // Check all 8 directions
  const directions = [
    { dr: 0, dc: 1 },   // Right
    { dr: 0, dc: -1 },  // Left
    { dr: 1, dc: 0 },   // Down
    { dr: -1, dc: 0 },  // Up
    { dr: 1, dc: 1 },   // Down-Right
    { dr: 1, dc: -1 },  // Down-Left
    { dr: -1, dc: 1 },  // Up-Right
    { dr: -1, dc: -1 }  // Up-Left
  ];
  
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      for (const dir of directions) {
        let found = true;
        let currentRow = row;
        let currentCol = col;
        
        // Check if word fits in this direction
        for (let i = 0; i < wordUpper.length; i++) {
          if (currentRow < 0 || currentRow >= grid.length || 
              currentCol < 0 || currentCol >= grid[row].length ||
              grid[currentRow][currentCol] !== wordUpper[i]) {
            found = false;
            break;
          }
          currentRow += dir.dr;
          currentCol += dir.dc;
        }
        
        if (found) {
          const endRow = row + dir.dr * (wordUpper.length - 1);
          const endCol = col + dir.dc * (wordUpper.length - 1);
          positions.push({
            start: { row, col },
            end: { row: endRow, col: endCol }
          });
        }
      }
    }
  }
  
  return positions;
};

// Generate word positions dynamically from the grid
const generateWordPositions = (): Word[] => {
  const words: Word[] = [];
  let colorIndex = 1;
  
  WORDS_TO_FIND.forEach(word => {
    const positions = findWordInGrid(word, SAMPLE_GRID);
    if (positions.length > 0) {
      // Use the first found position
      words.push({
        word,
        start: positions[0].start,
        end: positions[0].end,
        found: false,
        highlightColor: colorIndex
      });
      colorIndex = colorIndex > 7 ? 1 : colorIndex + 1;
    }
  });
  
  return words;
};

export default function WordSearchGame({ className }: WordSearchGameProps) {
  const [words, setWords] = useState<Word[]>(generateWordPositions());
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [currentSelection, setCurrentSelection] = useState<Position[]>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [foundWords, setFoundWords] = useState<Set<string>>(new Set());
  const [gameStarted, setGameStarted] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [canClose, setCanClose] = useState(false); // Add state to control if dialog can close
  // Timer effect
  useEffect(() => {
    if (gameStarted && !gameCompleted) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [gameStarted, gameCompleted]);

  // Check if game is completed
  useEffect(() => {
    if (foundWords.size === WORDS_TO_FIND.length && !gameCompleted) {
      setGameCompleted(true);
      setGameStarted(false);
      setIsOpen(true); // Automatically open dialog when puzzle is completed
    }
  }, [foundWords.size, gameCompleted]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const resetGame = () => {
    setWords(generateWordPositions());
    setSelectedCells(new Set());
    setCurrentSelection([]);
    setIsSelecting(false);
    setFoundWords(new Set());
    setGameStarted(false);
    setGameCompleted(false);
    setElapsedTime(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startGame = () => {
    if (!gameStarted && !gameCompleted) {
      setGameStarted(true);
    }
  };

  const getCellKey = (row: number, col: number) => `${row}-${col}`;

  const isInPath = (pos: Position, start: Position, end: Position): boolean => {
    const deltaRow = end.row - start.row;
    const deltaCol = end.col - start.col;
    const steps = Math.max(Math.abs(deltaRow), Math.abs(deltaCol));
    
    if (steps === 0) return pos.row === start.row && pos.col === start.col;
    
    const stepRow = deltaRow === 0 ? 0 : deltaRow / Math.abs(deltaRow);
    const stepCol = deltaCol === 0 ? 0 : deltaCol / Math.abs(deltaCol);
    
    for (let i = 0; i <= steps; i++) {
      const currentRow = start.row + stepRow * i;
      const currentCol = start.col + stepCol * i;
      if (currentRow === pos.row && currentCol === pos.col) {
        return true;
      }
    }
    return false;
  };

  const handleMouseDown = (row: number, col: number) => {
    if (!gameStarted && !gameCompleted) {
      startGame();
    }
    setIsSelecting(true);
    setCurrentSelection([{ row, col }]);
  };

  const handleMouseEnter = (row: number, col: number) => {
    if (isSelecting && currentSelection.length > 0) {
      const start = currentSelection[0];
      const newSelection = getPathCells(start, { row, col });
      setCurrentSelection(newSelection);
    }
  };

  const handleMouseUp = () => {
    if (currentSelection.length > 1) {
      checkForWord();
    }
    setIsSelecting(false);
    setCurrentSelection([]);
  };

  // Add touch event handlers for mobile
  const handleTouchStart = (row: number, col: number, e: React.TouchEvent) => {
    e.preventDefault(); // Prevent scrolling
    if (!gameStarted && !gameCompleted) {
      startGame();
    }
    setIsSelecting(true);
    setCurrentSelection([{ row, col }]);
  };

  // Remove the problematic handleTouchMove and replace with a better approach
  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault(); // Prevent scrolling
    
    if (!isSelecting || currentSelection.length === 0) return;
    
    // Get the touch position and find which cell it's over
    const touch = e.touches[0];
    if (!touch) return;
    
    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    // Calculate which cell the touch is over
    const cellSize = rect.width / GRID_SIZE;
    const col = Math.floor(x / cellSize);
    const row = Math.floor(y / cellSize);
    
    // Check if the touch is within grid bounds
    if (row >= 0 && row < GRID_SIZE && col >= 0 && col < GRID_SIZE) {
      const start = currentSelection[0];
      const newSelection = getPathCells(start, { row, col });
      setCurrentSelection(newSelection);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault(); // Prevent scrolling
    if (currentSelection.length > 1) {
      checkForWord();
    }
    setIsSelecting(false);
    setCurrentSelection([]);
  };

  // Add pointer event handlers for better cross-platform support
  const handlePointerDown = (row: number, col: number, e: React.PointerEvent) => {
    if (!gameStarted && !gameCompleted) {
      startGame();
    }
    setIsSelecting(true);
    setCurrentSelection([{ row, col }]);
  };

  const handlePointerEnter = (row: number, col: number) => {
    if (isSelecting && currentSelection.length > 0) {
      const start = currentSelection[0];
      const newSelection = getPathCells(start, { row, col });
      setCurrentSelection(newSelection);
    }
  };

  const handlePointerUp = () => {
    if (currentSelection.length > 1) {
      checkForWord();
    }
    setIsSelecting(false);
    setCurrentSelection([]);
  };

  const getPathCells = (start: Position, end: Position): Position[] => {
    const cells: Position[] = [];
    const deltaRow = end.row - start.row;
    const deltaCol = end.col - start.col;

    // Calculate the maximum distance in either direction
    const maxDistance = Math.max(Math.abs(deltaRow), Math.abs(deltaCol));
    
    if (maxDistance === 0) {
      return [start];
    }

    // Calculate step direction for each axis
    const stepRow = deltaRow === 0 ? 0 : deltaRow / Math.abs(deltaRow);
    const stepCol = deltaCol === 0 ? 0 : deltaCol / Math.abs(deltaCol);

    // Generate cells along the path
    for (let i = 0; i <= maxDistance; i++) {
      const row = start.row + stepRow * i;
      const col = start.col + stepCol * i;
      
      // Ensure we stay within grid bounds
      if (row >= 0 && row < GRID_SIZE && col >= 0 && col < GRID_SIZE) {
        cells.push({ row, col });
      }
    }

    return cells;
  };

  const checkForWord = () => {
    if (currentSelection.length < 2) return;
    
    const start = currentSelection[0];
    const end = currentSelection[currentSelection.length - 1];
    const selectedWord = currentSelection.map(pos => SAMPLE_GRID[pos.row][pos.col]).join('');
    const reversedWord = selectedWord.split('').reverse().join('');
    
    setWords(prevWords => {
      return prevWords.map(word => {
        if (!word.found && (word.word === selectedWord || word.word === reversedWord)) {
          const pathMatches = isInPath(start, word.start, word.end) && isInPath(end, word.start, word.end);
          const reversePathMatches = isInPath(start, word.end, word.start) && isInPath(end, word.end, word.start);
          
          if (pathMatches || reversePathMatches) {
            setFoundWords(prev => new Set(Array.from(prev).concat(word.word)));
            return { ...word, found: true };
          }
        }
        return word;
      });
    });
  };

  const isCurrentlySelected = (row: number, col: number): boolean => {
    return currentSelection.some(pos => pos.row === row && pos.col === col);
  };

  const getHighlightClass = (colorNumber: number): string => {
    switch (colorNumber) {
      case 1: return 'bg-highlight-1';
      case 2: return 'bg-highlight-2';
      case 3: return 'bg-highlight-3';
      case 4: return 'bg-highlight-4';
      case 5: return 'bg-highlight-5';
      case 6: return 'bg-highlight-6';
      case 7: return 'bg-highlight-7';
      case 8: return 'bg-highlight-8';
      default: return 'bg-highlight-1';
    }
  };

  // Function to get the brush-like highlight style for current selection
  const getSelectionHighlightStyle = (row: number, col: number) => {
    if (currentSelection.length < 2) return {};
    
    const start = currentSelection[0];
    const end = currentSelection[currentSelection.length - 1];
    const deltaRow = end.row - start.row;
    const deltaCol = end.col - start.col;
    
    // Calculate position within the selection
    const posInSelection = currentSelection.findIndex(pos => pos.row === row && pos.col === col);
    if (posInSelection === -1) return {};
    
    const totalLength = currentSelection.length;
    const progress = posInSelection / (totalLength - 1);
    
    // Create a gradient-like effect
    const opacity = 0.3 + (progress * 0.4); // Varying opacity for brush effect
    
    return {
      background: `linear-gradient(45deg, rgba(59, 130, 246, ${opacity}), rgba(147, 51, 234, ${opacity}))`,
      borderRadius: '4px',
      transform: 'scale(1.05)',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    };
  };

  // Function to get the word highlight style for found words
  const getWordHighlightStyle = (row: number, col: number) => {
    for (const word of words) {
      if (word.found && isInPath({ row, col }, word.start, word.end)) {
        const colors = [
          'rgba(34, 197, 94, 0.4)',   // Green
          'rgba(59, 130, 246, 0.4)',  // Blue
          'rgba(168, 85, 247, 0.4)',  // Purple
          'rgba(236, 72, 153, 0.4)',  // Pink
          'rgba(251, 191, 36, 0.4)',  // Yellow
          'rgba(239, 68, 68, 0.4)',   // Red
          'rgba(16, 185, 129, 0.4)',  // Emerald
          'rgba(245, 158, 11, 0.4)'   // Amber
        ];
        
        return {
          background: colors[word.highlightColor - 1] || colors[0],
          borderRadius: '4px',
          transition: 'all 0.3s ease'
        };
      }
    }
    return {};
  };

  // Function to handle form submission success
  const handleFormSuccess = () => {
    setCanClose(true);
    setTimeout(() => {
      setIsOpen(false);
    }, 2000); // Close after 2 seconds
  };

  return (
    <>
    
  <section className={`hero-section solutions-child-page puzzle-game-page`}>
      <div className={'container'}>
          <div className={'section-tag testimonial pd-right'}><MwSmallIcon/> Game</div>
          <h1>Welcome to <span className='text-gradient'> Observability Puzzle Game! </span></h1>
          <p>Compete with fellow DevOps engineers to win a Kubernetes certification voucher by cracking this word <br/> search puzzle filled with observability concepts.</p>
      </div>
    
    <div className='sub-points-puzzle'>
      <div className='container'>
        <div className='d-flex'>
          <div className='puzzle-step-card'>
            <img src='/images/find-words.png' alt='puzzle-icon-1' width={100} height={100} />
            <h5>Find Words</h5>
            <p>Look for the words listed on the right-hand side</p>
          </div>
          <div className='puzzle-step-card'>
            <img src='/images/select-words.png' alt='puzzle-icon-1' width={100} height={100} />
            <h5>Select Words</h5>
            <p>Click on the correct word; it will change color</p>
          </div>
          <div className='puzzle-step-card'>
            <img src='/images/complete-all.png' alt='puzzle-icon-1' width={100} height={100} />
            <h5>Complete All</h5>
            <p>Find all the words and fill the form</p>
          </div>
          <div className='puzzle-step-card'>
            <img src='/images/win-k8s.png' alt='puzzle-icon-1' width={100} height={100} />
            <h5>Win <br/> Kubernetes Certification! <span>(CKA/CKAD/CKS)</span></h5>
          </div>
        </div>
      </div>
    </div>

    <div className="container puzzle-container-mw">
      <div className="max-w-7xl mx-auto">
        {/* Game Header with Timer and Reset */}
        

        <div className="puzzle-mw">
          {/* Word Search Grid */}
          
          <div className="puzzle-card">
          <div className='top-clock'>
            <h4>
              Observability Puzzle Game
            </h4>
            <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="clock-mw">
              <span className="clock-text">
                {formatTime(elapsedTime)}
              </span>
            </div>
            {gameCompleted && (
              <div className="bg-green-100 text-green-800 px-4 py-2 rounded-lg border border-green-200">
                <span className="font-semibold">🎉 Completed in {formatTime(elapsedTime)}!</span>
              </div>
            )}
          </div>
        </div>
        </div>
            
            <div 
              ref={gridRef}
              className="inline-block bg-cell-bg rounded-lg border border-puzzle-border p-4 select-none relative"
              onMouseLeave={() => {
                if (isSelecting) {
                  setIsSelecting(false);
                  setCurrentSelection([]);
                }
              }}
              onTouchMove={handleTouchMove} // Move this to the container level
              onTouchEnd={() => {
                if (isSelecting) {
                  setIsSelecting(false);
                  setCurrentSelection([]);
                }
              }}
              style={{ touchAction: 'none' }} // Prevent default touch behaviors
            >
              <div className="grid grid-cols-15 relative">
                {SAMPLE_GRID.map((row, rowIndex) =>
                  row.map((letter, colIndex) => {
                    const isSelected = isCurrentlySelected(rowIndex, colIndex);
                    const selectionStyle = isSelected ? getSelectionHighlightStyle(rowIndex, colIndex) : {};
                    const wordStyle = getWordHighlightStyle(rowIndex, colIndex);
                    
                    return (
                      <div
                        key={getCellKey(rowIndex, colIndex)}
                        className="puzzle-cell hover:bg-cell-hover text-cell-text"
                        style={{
                          ...selectionStyle,
                          ...wordStyle,
                          touchAction: 'none', // Prevent scrolling and zooming
                          WebkitTouchCallout: 'none', // Disable iOS touch callout
                          WebkitUserSelect: 'none', // Disable text selection
                          userSelect: 'none'
                        }}
                        onMouseDown={() => handleMouseDown(rowIndex, colIndex)}
                        onMouseEnter={() => handleMouseEnter(rowIndex, colIndex)}
                        onMouseUp={handleMouseUp}
                        onTouchStart={(e) => handleTouchStart(rowIndex, colIndex, e)}
                        onTouchEnd={handleTouchEnd}
                        onPointerDown={(e) => handlePointerDown(rowIndex, colIndex, e)}
                        onPointerEnter={() => handlePointerEnter(rowIndex, colIndex)}
                        onPointerUp={handlePointerUp}
                      >
                        {letter}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Word List Sidebar */}
          <div className="puzzle-content">
          <h4>
            Find These Words
          </h4>
            <div className="these-words-list">
              
              
              <div className="space-y-2">
                {WORDS_TO_FIND.map((word) => {
                  const isFound = foundWords.has(word);
                  return (
                    <div
                      key={word}
                      className={isFound ? "puzzle-word-list bg-highlight-2" : "puzzle-word-list bg-card hover:bg-cell-hover text-word-pending"}
                    >
                      <span className={isFound ? "line-through text-word-found" : ""}>
                        {word}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 pt-4 border-t border-puzzle-border">
                <div className="found-bar">
                  
                  <div className="w-full bg-muted rounded-full h-2 mt-2">
                    <div 
                      className="bar bg-word-found h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(foundWords.size / WORDS_TO_FIND.length) * 100}%` }}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Found: <span className="font-bold text-word-found">{foundWords.size}</span> / {WORDS_TO_FIND.length}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
        </div>
        <button
            onClick={resetGame}
            className="flex items-center gap-2 bg-card hover:bg-cell-hover text-foreground px-4 py-2 rounded-lg border border-puzzle-border transition-colors duration-200"
          >
            {/* <RotateCcw className="w-4 h-4" /> */}
            Reset Game
          </button>
          <button className='button primary'
            onClick={() => setIsOpen(true)}
          >
            Show Form
          </button>
      </div>
    </div>

    </section>
    <CallToActionSmall/>

    <Dialog
          isOpen={isOpen}
          position={"center"}
          onClose={() => {
              // Only allow closing if form was submitted successfully
              if (canClose) {
                  setIsOpen(false);
              }
          }}
          size={"sm"}
          verticalCenter={true}
      >
          <div style={{ position: 'relative' }}>
              <PuzzleGameForm onSuccess={handleFormSuccess} />
              {/* Hide the close button with CSS */}
              <style jsx>{`
                  :global(.dialog__close-action) {
                      display: none !important;
                  }
              `}</style>
          </div>
      </Dialog>
    </>
  );
}
